export interface CommercialInvoiceUrl {
  url: string;
  source: "custom" | "easypost";
}

interface ShipmentWithInvoiceData {
  customs_forms?: { commercial_invoices?: { url: string }[] | null } | null;
  _shipment?: { forms?: { form_type?: string; form_url?: string }[] };
}
/** Gets the preferred custom or EasyPost commercial-invoice URL. */
export function getCommercialInvoiceUrl(
  shipment: ShipmentWithInvoiceData | undefined | null,
): CommercialInvoiceUrl | null {
  if (!shipment) {
    return null;
  }

  // First check for custom commercial invoice (our generated form)
  const customInvoice = shipment.customs_forms?.commercial_invoices?.[0];
  if (customInvoice?.url) {
    return { url: customInvoice.url, source: "custom" };
  }

  // Fall back to EasyPost-generated commercial invoice
  const easypostInvoice = shipment._shipment?.forms?.find(
    (form: { form_type?: string }) => form.form_type === "commercial_invoice",
  );
  if (easypostInvoice?.form_url) {
    return { url: easypostInvoice.form_url, source: "easypost" };
  }

  return null;
}

interface CustomsFormsLike<T> {
  commercial_invoices?: T[] | null;
}

const createdAtMs = (invoice: { created_at?: Date | null }): number =>
  invoice.created_at?.getTime() ?? 0;

/** Newest first, since every reader resolves `commercial_invoices[0]`; a late write of an older invoice never displaces a newer one. */
export function prependCommercialInvoice<
  T extends { created_at?: Date | null },
>(
  existing: CustomsFormsLike<T> | null | undefined,
  invoice: T,
): { commercial_invoices: T[] } {
  return {
    commercial_invoices: [
      invoice,
      ...(existing?.commercial_invoices ?? []),
    ].sort((a, b) => createdAtMs(b) - createdAtMs(a)),
  };
}

export interface InvoiceBearingShipment<T> {
  _shipment?: { id?: string | null } | null;
  shipmentGroupID?: string | null;
  customs_forms?: CustomsFormsLike<T> | null;
  /** The declaration the parcel's invoice was rendered from; a regenerated
   * invoice moves it forward so a later build reuses the corrected snapshot. */
  customsDeclarationId?: string | null;
}

export interface RegeneratedInvoiceWrite<T> {
  shipmentIndex: number;
  shipmentId?: string | null;
  shipmentGroupID?: string | null;
  invoice: T;
}

/** `return_.shipment` mirrors one entry of `shipments[]`. Label ids name it; without them a group only identifies the mirror when it holds a single parcel. */
export function isInvoiceShipmentMirror<T>(
  mirror: InvoiceBearingShipment<T>,
  shipment: InvoiceBearingShipment<T>,
  shipments: readonly InvoiceBearingShipment<T>[],
): boolean {
  if (mirror._shipment?.id && shipment._shipment?.id) {
    return mirror._shipment.id === shipment._shipment.id;
  }
  if (
    !mirror.shipmentGroupID ||
    mirror.shipmentGroupID !== shipment.shipmentGroupID
  ) {
    return false;
  }
  return (
    shipments.filter((s) => s.shipmentGroupID === shipment.shipmentGroupID)
      .length === 1
  );
}

/** The parcel a write was built for. A label id that is gone means the parcel was replaced, so the invoice is stale: never fall back to a sibling. */
function resolveTarget<T>(
  shipments: readonly InvoiceBearingShipment<T>[],
  write: RegeneratedInvoiceWrite<T>,
): InvoiceBearingShipment<T> | undefined {
  if (write.shipmentId) {
    return shipments.find((s) => s._shipment?.id === write.shipmentId);
  }
  const byIndex = shipments[write.shipmentIndex];
  if (
    byIndex &&
    (!write.shipmentGroupID ||
      byIndex.shipmentGroupID === write.shipmentGroupID)
  ) {
    return byIndex;
  }
  const inGroup = write.shipmentGroupID
    ? shipments.filter((s) => s.shipmentGroupID === write.shipmentGroupID)
    : [];
  return inGroup.length === 1 ? inGroup[0] : undefined;
}

/** Writes each regenerated invoice onto the parcel it was built for (and onto the mirrored `shipment`); returns the writes whose parcel no longer exists. */
export function placeRegeneratedInvoices<
  T extends { created_at?: Date | null; customsDeclarationId?: string | null },
>(
  return_: {
    shipment?: InvoiceBearingShipment<T> | null;
    shipments?: InvoiceBearingShipment<T>[] | null;
  },
  writes: readonly RegeneratedInvoiceWrite<T>[],
): RegeneratedInvoiceWrite<T>[] {
  const shipments = return_.shipments ?? [];
  const mirror = return_.shipment;
  const unplaced: RegeneratedInvoiceWrite<T>[] = [];
  const apply = (shipment: InvoiceBearingShipment<T>, invoice: T) => {
    shipment.customs_forms = prependCommercialInvoice(
      shipment.customs_forms,
      invoice,
    );
    if (invoice.customsDeclarationId) {
      shipment.customsDeclarationId = invoice.customsDeclarationId;
    }
  };
  for (const write of writes) {
    const { invoice } = write;
    if (shipments.length === 0) {
      // A write that names a label belongs to that parcel; a replaced mirror is a different one.
      const isTarget = write.shipmentId
        ? mirror?._shipment?.id === write.shipmentId
        : write.shipmentIndex === 0;
      if (isTarget && mirror) {
        apply(mirror, invoice);
      } else {
        unplaced.push(write);
      }
      continue;
    }
    const target = resolveTarget(shipments, write);
    if (!target) {
      unplaced.push(write);
      continue;
    }
    apply(target, invoice);
    if (mirror && isInvoiceShipmentMirror(mirror, target, shipments)) {
      apply(mirror, invoice);
    }
  }
  return unplaced;
}
